import { Button, Checkbox, Group, Paper, Stack, Text, TextInput } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { FREIGHT_CLASS_OPTIONS } from './freight-class-options';
import { freightFormMessages } from './freight-form-messages';
import { validationMessages } from '../../../../address/validation-messages';
import { getMissingRequiredHazmatFieldsForCommodity } from '../../../../ltl/hazmat-validation';
import { WeightInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { CommodityHazmatInfo } from '../../../freight-booking-wizard/components/handling-units-step/CommodityHazmatInfo';
import { handlingUnitsStepMessages } from '../../../freight-booking-wizard/components/handling-units-step/handling-units-step-messages';

import type { FreightClass, LtlCommodity } from '../../../../types/ltl';
import type { Weight } from '../../../../types/weight';

type CommodityCardProps = {
  commodity: LtlCommodity;
  index: number;
  isHazmatEnabled?: boolean;
  onChange: (updated: LtlCommodity) => void;
  onRemove?: () => void;
};

export const CommodityCard = ({ commodity, index, isHazmatEnabled = false, onChange, onRemove }: CommodityCardProps) => {
  const intl = useIntl();

  const hazmatFieldErrors: Record<string, string> | undefined =
    isHazmatEnabled && commodity.isHazmat
      ? Object.fromEntries(
          [...getMissingRequiredHazmatFieldsForCommodity(commodity)].map((field) => [
            field,
            intl.formatMessage(validationMessages.required),
          ]),
        )
      : undefined;

  const weightValue: Weight = {
    value: commodity.weight,
    unit: commodity.weightUnit ?? 'Ounces',
  };

  const handleWeightChange = (next: Weight) => {
    onChange({ ...commodity, weight: next.value });
  };

  const handleClassChange = (val: string | null) => {
    onChange({ ...commodity, freightClass: (val as FreightClass) ?? '' });
  };

  return (
    <Paper bg='gray.0' data-testid={`commodity-card-${index}`} p='sm' withBorder>
      <Stack gap='xs'>
        <Group justify='space-between'>
          <Text fw={500} size='sm'>
            {intl.formatMessage(freightFormMessages.commodityTitle, { number: index + 1 })}
          </Text>
          {onRemove ? (
            <Button color='red' data-testid={`remove-commodity-${index}`} onClick={onRemove} size='xs' variant='ghost'>
              <FormattedMessage {...freightFormMessages.removeCommodity} />
            </Button>
          ) : null}
        </Group>
        <Group grow>
          <WeightInput onBlur={handleWeightChange} showMinorUnit={false} value={weightValue} withAsterisk />
          <Dropdown
            data={FREIGHT_CLASS_OPTIONS}
            data-testid={`commodity-class-${index}`}
            label={intl.formatMessage(freightFormMessages.freightClassLabel)}
            onChange={handleClassChange}
            placeholder={intl.formatMessage(freightFormMessages.freightClassPlaceholder)}
            value={commodity.freightClass || null}
            withAsterisk
          />
        </Group>
        <TextInput
          data-testid={`commodity-description-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.commodityDescriptionLabel)}
          onChange={(event) => onChange({ ...commodity, description: event.currentTarget.value })}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.commodityDescriptionPlaceholder)}
          value={commodity.description}
        />
        {isHazmatEnabled && (
          <>
            <Checkbox
              checked={commodity.isHazmat}
              data-testid={`commodity-hazmat-${index}`}
              label={intl.formatMessage(handlingUnitsStepMessages.hazmatCheckboxLabel)}
              mt='sm'
              onChange={(event) => onChange({ ...commodity, isHazmat: event.currentTarget.checked })}
            />
            {commodity.isHazmat && (
              <CommodityHazmatInfo
                commodity={commodity}
                fieldErrors={hazmatFieldErrors}
                index={index}
                onChange={onChange}
                showOptionalFields={false}
              />
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
